import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clipboard, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

type Policy = {
  enabled: boolean;
  offer_type: "fixed_credit" | "percentage_discount";
  value: number;
  currency: string;
  label: string;
  eligible_categories: string[];
  max_rating: number;
  expiry_days: number;
  max_per_contact_90_days: number;
  terms: string | null;
};
type ConnectionState = {
  email: { configured: boolean; enabled: boolean; address: string | null; missing?: string[] };
  recovery_policy: Policy;
};
type GoogleState = {
  configured: boolean;
  connected: boolean;
  status: string;
  locations: Array<{ location_name: string; title: string; enabled: boolean; last_synced_at: string | null; last_error: string | null }>;
};

const CATEGORIES = [
  ["service", "Service"],
  ["booking", "Booking"],
  ["operational", "Operations"],
  ["billing", "Billing"],
  ["other", "Other"],
] as const;

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data: session } = await supabase.auth.getSession();
  const { data, error } = await supabase.functions.invoke(name, {
    body,
    headers: session.session ? { Authorization: `Bearer ${session.session.access_token}` } : undefined,
  });
  if (error) {
    try {
      const payload = await (error as { context?: Response }).context?.json();
      throw new Error(String(payload?.detail ?? payload?.error ?? error.message));
    } catch (parsed) {
      if (parsed instanceof Error && parsed.message !== "Unexpected end of JSON input") throw parsed;
      throw error;
    }
  }
  if (data?.error) throw new Error(String(data.error));
  return data as T;
}

export default function ComplaintRecoverySettings() {
  const { businessId } = useAuth();
  const queryClient = useQueryClient();
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [redemptionCode, setRedemptionCode] = useState("");

  const connections = useQuery({
    queryKey: ["complaint-connections", businessId],
    queryFn: () => invoke<ConnectionState>("complaint-connections", { action: "list" }),
    enabled: !!businessId,
  });
  const google = useQuery({
    queryKey: ["google-business-profile", businessId],
    queryFn: () => invoke<GoogleState>("google-business-profile", { action: "status" }),
    enabled: !!businessId,
  });
  useEffect(() => {
    if (connections.data?.recovery_policy) setPolicy({ ...connections.data.recovery_policy, value: Number(connections.data.recovery_policy.value) });
  }, [connections.data?.recovery_policy]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["complaint-connections", businessId] }),
      queryClient.invalidateQueries({ queryKey: ["google-business-profile", businessId] }),
    ]);
  };
  const run = async (key: string, task: () => Promise<unknown>, success: string) => {
    setBusy(key);
    try {
      await task();
      toast.success(success);
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message.replace(/_/g, " ") : "That did not work yet");
    } finally {
      setBusy(null);
    }
  };

  const startGoogle = () => run("google-connect", async () => {
    const redirectUri = `${window.location.origin}/oauth/google-business`;
    const result = await invoke<{ authorization_url: string }>("google-business-profile", { action: "start_oauth", redirect_uri: redirectUri });
    window.location.assign(result.authorization_url);
  }, "Opening Google…");

  if (connections.isLoading || google.isLoading || !policy) {
    return <div className="flex items-center justify-center rounded-xl border p-8 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading complaint recovery setup…</div>;
  }

  const email = connections.data?.email;
  const googleState = google.data;
  const selectedLocations = googleState?.locations.filter((row) => row.enabled).map((row) => row.location_name) ?? [];
  const updateLocation = (name: string, enabled: boolean) => {
    const next = enabled ? [...new Set([...selectedLocations, name])] : selectedLocations.filter((item) => item !== name);
    return run("google-locations", () => invoke("google-business-profile", { action: "set_locations", location_names: next }), "Google locations updated.");
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Complaint sources</h3>
        <p className="mt-1 text-xs text-muted-foreground">Connect the places where customer complaints arrive and choose which locations to monitor.</p>
      </div>
      <div className="grid gap-3 xl:grid-cols-3">
        <Card className="shadow-none">
          <CardHeader className="pb-3"><CardTitle className="flex items-center justify-between text-sm"><span>Complaint email</span><Badge variant={email?.configured ? "secondary" : "outline"}>{email?.configured ? "Ready" : "Setup needed"}</Badge></CardTitle></CardHeader>
          <CardContent className="space-y-3 text-xs">
            {email?.configured && email.address ? <>
              <p className="text-muted-foreground">Forward complaint emails to this private address.</p>
              <div className="flex gap-2"><Input readOnly value={email.address} className="h-9 text-xs" /><Button size="icon" variant="outline" onClick={() => { void navigator.clipboard.writeText(email.address!); toast.success("Email address copied."); }}><Clipboard className="h-4 w-4" /></Button></div>
              <div className="flex items-center justify-between"><Label className="text-xs">Accept new complaint emails</Label><Switch checked={email.enabled} disabled={busy === "email"} onCheckedChange={(enabled) => run("email", () => invoke("complaint-connections", { action: "set_email_enabled", enabled }), enabled ? "Email intake enabled." : "Email intake paused.")} /></div>
            </> : <p className="rounded-lg bg-amber-50 p-3 leading-relaxed text-amber-800">Dibs needs a Resend receiving domain and signed webhook secret before email intake can be switched on. Until then, no unsigned email can create a case.</p>}
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardHeader className="pb-3"><CardTitle className="flex items-center justify-between text-sm"><span>Google reviews</span><Badge variant={googleState?.connected ? "secondary" : "outline"}>{googleState?.connected ? "Connected" : googleState?.configured ? "Not connected" : "Setup needed"}</Badge></CardTitle></CardHeader>
          <CardContent className="space-y-3 text-xs">
            {!googleState?.configured ? <p className="rounded-lg bg-amber-50 p-3 leading-relaxed text-amber-800">Google Business Profile API access and OAuth credentials must be added to production first.</p> : !googleState.connected ? <Button size="sm" className="gap-1.5" disabled={busy === "google-connect"} onClick={startGoogle}><ExternalLink className="h-3.5 w-3.5" /> Connect Google</Button> : <>
              <p className="text-muted-foreground">Only enabled locations are checked for low or sensitive reviews.</p>
              <div className="max-h-32 space-y-2 overflow-y-auto">{googleState.locations.map((location) => <label key={location.location_name} className="flex items-center gap-2"><Checkbox checked={location.enabled} disabled={busy === "google-locations"} onCheckedChange={(checked) => updateLocation(location.location_name, checked === true)} /><span className="truncate">{location.title}</span></label>)}</div>
              <div className="flex gap-2"><Button size="sm" variant="outline" className="gap-1.5" disabled={busy === "google-sync"} onClick={() => run("google-sync", () => invoke("google-business-profile", { action: "sync_now" }), "Google review sync requested.")}><RefreshCw className="h-3.5 w-3.5" /> Sync now</Button><Button size="sm" variant="ghost" disabled={busy === "google-disconnect"} onClick={() => run("google-disconnect", () => invoke("google-business-profile", { action: "disconnect" }), "Google disconnected.")}>Disconnect</Button></div>
            </>}
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardHeader className="pb-3"><CardTitle className="flex items-center justify-between text-sm"><span>Redeem a code</span><Badge variant="outline">One use</Badge></CardTitle></CardHeader>
          <CardContent className="space-y-3 text-xs"><p className="text-muted-foreground">When a customer uses an approved recovery code, mark it used here.</p><div className="flex gap-2"><Input value={redemptionCode} onChange={(event) => setRedemptionCode(event.target.value.toUpperCase())} placeholder="DIBS-1234ABCD" className="h-9" /><Button size="sm" disabled={busy === "redeem" || !/^DIBS-[A-F0-9]{8}$/.test(redemptionCode)} onClick={() => run("redeem", () => invoke("complaint-connections", { action: "redeem_offer", code: redemptionCode }), "Recovery code redeemed.").then(() => setRedemptionCode(""))}>Redeem</Button></div></CardContent>
        </Card>
      </div>

      <Card className="shadow-none">
        <CardHeader className="pb-3"><CardTitle className="flex items-center justify-between text-sm"><span>Recovery offer limits</span><div className="flex items-center gap-2"><Label className="text-xs">Allow proposals</Label><Switch checked={policy.enabled} onCheckedChange={(enabled) => setPolicy({ ...policy, enabled })} /></div></CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="border-l-2 border-border pl-3 text-xs leading-relaxed text-muted-foreground">Dibs never sends compensation by itself. It checks these limits first, then a person must approve the offer. Health, safety, privacy, high-risk and emergency complaints are always excluded.</p>
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
            <div className="space-y-1.5"><Label className="text-xs">Offer type</Label><Select value={policy.offer_type} onValueChange={(offer_type: Policy["offer_type"]) => setPolicy({ ...policy, offer_type })}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="fixed_credit">Fixed credit</SelectItem><SelectItem value="percentage_discount">Percentage off</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label className="text-xs">Maximum value</Label><Input type="number" min="0" max={policy.offer_type === "percentage_discount" ? 100 : undefined} value={policy.value} onChange={(event) => setPolicy({ ...policy, value: Number(event.target.value) })} className="h-9" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Currency</Label><Input value={policy.currency} maxLength={3} onChange={(event) => setPolicy({ ...policy, currency: event.target.value.toUpperCase() })} disabled={policy.offer_type === "percentage_discount"} className="h-9" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Rating at or below</Label><Select value={String(policy.max_rating)} onValueChange={(value) => setPolicy({ ...policy, max_rating: Number(value) })}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent>{[1, 2, 3].map((value) => <SelectItem key={value} value={String(value)}>{value} stars</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label className="text-xs">Expires after</Label><Input type="number" min="1" max="365" value={policy.expiry_days} onChange={(event) => setPolicy({ ...policy, expiry_days: Number(event.target.value) })} className="h-9" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Per customer / 90 days</Label><Input type="number" min="0" max="10" value={policy.max_per_contact_90_days} onChange={(event) => setPolicy({ ...policy, max_per_contact_90_days: Number(event.target.value) })} className="h-9" /></div>
          </div>
          <div className="grid gap-3 md:grid-cols-2"><div className="space-y-1.5"><Label className="text-xs">Customer-facing name</Label><Input value={policy.label} onChange={(event) => setPolicy({ ...policy, label: event.target.value })} /></div><div className="space-y-1.5"><Label className="text-xs">Terms</Label><Textarea rows={2} value={policy.terms ?? ""} onChange={(event) => setPolicy({ ...policy, terms: event.target.value || null })} /></div></div>
          <div><Label className="text-xs">Eligible complaint types</Label><div className="mt-2 flex flex-wrap gap-4">{CATEGORIES.map(([value, label]) => <label key={value} className="flex items-center gap-2 text-xs"><Checkbox checked={policy.eligible_categories.includes(value)} onCheckedChange={(checked) => setPolicy({ ...policy, eligible_categories: checked ? [...policy.eligible_categories, value] : policy.eligible_categories.filter((item) => item !== value) })} />{label}</label>)}</div></div>
          <div className="flex justify-end"><Button size="sm" disabled={busy === "policy" || !policy.eligible_categories.length} onClick={() => run("policy", () => invoke("complaint-connections", { action: "set_recovery_policy", ...policy }), policy.enabled ? "Recovery guardrails saved." : "Recovery offers remain switched off.")}>{busy === "policy" ? "Saving…" : "Save guardrails"}</Button></div>
        </CardContent>
      </Card>
    </div>
  );
}
